import { useState } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Select,
  Button,
  Alert,
} from '@kollektive/design-system';
import styles from './InviteModal.module.css';

interface InviteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onInvite: (email: string, role: string) => Promise<void>;
  isLoading: boolean;
}

const ROLE_OPTIONS = [
  { value: 'member', label: 'Member' },
  { value: 'admin', label: 'Admin' },
  { value: 'viewer', label: 'Viewer' },
];

// Email validation regex - requires valid format with TLD of at least 2 chars
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;

function isValidEmail(email: string): boolean {
  return EMAIL_REGEX.test(email.trim().toLowerCase());
}

export function InviteModal({
  isOpen,
  onClose,
  onInvite,
  isLoading,
}: InviteModalProps) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('member');
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);

  const emailValid = isValidEmail(email);
  const showEmailError = touched && email.trim().length > 0 && !emailValid;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const trimmedEmail = email.trim().toLowerCase();

    if (!trimmedEmail) {
      setError('Email is required');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      await onInvite(trimmedEmail, role);
      setEmail('');
      setRole('member');
      setTouched(false);
    } catch (err) {
      if (err instanceof Error) {
        // Map backend error codes to user-friendly messages
        if (err.message.includes('INVITATION_EXISTS')) {
          setError('A pending invitation already exists for this email');
        } else if (err.message.includes('USER_EXISTS')) {
          setError('This user is already a member of the workspace');
        } else if (err.message.includes('FORBIDDEN')) {
          setError('You do not have permission to send this invitation');
        } else {
          setError(err.message);
        }
      } else {
        setError('Failed to send invitation');
      }
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setEmail('');
      setRole('member');
      setError(null);
      setTouched(false);
      onClose();
    }
  };

  return (
    <Modal open={isOpen} onClose={handleClose} size="sm">
      <form onSubmit={handleSubmit}>
        <ModalHeader title="Invite Team Member" onClose={handleClose} />
        <ModalBody>
          <div className={styles.formFields}>
            <Input
              label="Email Address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={() => setTouched(true)}
              placeholder="colleague@company.com"
              disabled={isLoading}
              error={showEmailError}
              helpText={showEmailError ? 'Please enter a valid email address' : undefined}
              autoFocus
            />

            <Select
              label="Role"
              options={ROLE_OPTIONS}
              value={role}
              onChange={(value) => setRole(value)}
              disabled={isLoading}
            />

            <p className={styles.roleDescription}>
              {role === 'admin' && 'Admins can manage team members and most settings.'}
              {role === 'member' && 'Members can create and manage connections and tokens.'}
              {role === 'viewer' && 'Viewers have read-only access to workspace data.'}
            </p>

            {error && (
              <Alert variant="error" styleVariant="glass">
                {error}
              </Alert>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isLoading || !emailValid}
          >
            {isLoading ? 'Sending...' : 'Send Invitation'}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}
